import { EXPRESS_API } from '../../configs/api/expressApi';
import { useMutation } from 'react-query';
import { useAuth } from '../../provider/auth';
import { errorToast } from '../../toasts/error';
import { successToast } from '../../toasts/success';
import { MarkAsSentOptions } from '../../types';
import { useAI } from '../provider/AiProvider';
import { GetQuickshareParams } from '../types';
import { copyClipboard, isSafari } from '../../utils/copy';

export interface MarkAsSentResponse {
  message: string;
}

export interface IQuickShareMarkAsSentParams {
  videoId: number | string;
  linksetId?: number | string;
  templateId?: number | string;
  overlayId?: number | string;
  vin?: number | string;

  markAsSentConfirmed?: boolean | undefined;
  videoActivityType?: string | undefined;
  iconId?: number | string;
  format?: string;
  useAutomotive?: string | boolean | undefined;
  allowVideoReply?: string | boolean | undefined;
  allowReactions?: string | boolean | undefined;
  notifyUser?: string | boolean | undefined;
  toastType?: 'copy' | 'insert' | null;
  reuseShortUrl?: boolean;
  generatedText?: string;
}

const markShorUrlAsSent = async (params: IQuickShareMarkAsSentParams) => {
  const { videoId, toastType: _, ...rest } = params;

  const body = {
    ...rest,
    ...(isSafari ? { reuseShortUrl: true } : {}),
    ignoreCrmProvider: true,
  };

  const { data } = await EXPRESS_API.post(
    `share/quickshare/${videoId}/mark-as-sent`,
    body
  );

  return data;
};

interface IProps {
  handleSubmitGeneratedContent?:
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  handleOnCopySuccess?:
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  onSuccessCallback: () => void;
  data: {
    generatedText: string;
    generatedUrl: string;
    generatedThumnail: string;
  };
}

export const useMarkShorUrlAsSentMutation = ({
  handleSubmitGeneratedContent,
  handleOnCopySuccess,
  data,
  onSuccessCallback,
}: IProps) => {
  const { params } = useAI();

  const { userData } = useAuth();
  const markAsSent = !userData.isAutomotive
    ? MarkAsSentOptions.NEVER.value
    : userData.customer.markVideosAsSent === null
    ? MarkAsSentOptions.ASK_VERY_TIME.value
    : parseInt(userData.customer.markVideosAsSent.toString(), 10);
  return useMutation(
    (quickShareParams: IQuickShareMarkAsSentParams) =>
      markShorUrlAsSent({ ...quickShareParams, ...params }),
    {
      onSuccess: async (_: MarkAsSentResponse, variables) => {
        const { generatedText, generatedUrl, generatedThumnail } = data;
        if (markAsSent === MarkAsSentOptions.ALWAYS.value) {
          if (userData.isAutomotive) {
            successToast({
              title: `Video marked as Sent`,
            });
          }
        }

        if (markAsSent === MarkAsSentOptions.ASK_VERY_TIME.value) {
          variables.markAsSentConfirmed &&
            userData.isAutomotive &&
            successToast({ title: 'Video marked as Sent' });
        }

        if (!isSafari) {
          copyClipboard(
            data.generatedUrl,
            data?.generatedText || '',
            {} as IQuickShareMarkAsSentParams
          );
        }

        if (variables.toastType === 'copy') {
          successToast({ title: 'CRM Code Copied' });
          handleOnCopySuccess?.(
            generatedText,
            generatedUrl,
            generatedThumnail,
            !!variables.markAsSentConfirmed,
            params
          );
        }

        if (variables.toastType === 'insert') {
          handleSubmitGeneratedContent?.(
            generatedText,
            generatedUrl,
            generatedThumnail,
            !!variables.markAsSentConfirmed,
            params
          );
        }

        onSuccessCallback();
      },
      onError: () => {
        errorToast({ title: 'Failed to retrieve quickshare link!' });
      },
    }
  );
};
